import "@/print.css";
import { Children } from "react";
import { BSM_LOGO_PRINT } from "./logoData";

const esc = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/"/g, '\\"').slice(0, 90);

export function PrintPageStyle({ title, date, rtl = false, pageWord = "Side", ofWord = "af" }) {
  const left = `content: "BSM A/S"; font: 700 8pt Manrope, sans-serif; letter-spacing: 0.14em; color: #A83232;`;
  const right = `content: "${esc(title)}${date ? ` · ${esc(date)}` : ""}"; font: 500 8pt Manrope, sans-serif; color: #A83232;`;
  const css = `@media print { @page {
    @top-left { ${rtl ? right : left} vertical-align: bottom; padding-bottom: 5mm; }
    @top-center { content: none; }
    @top-right { ${rtl ? left : right} vertical-align: bottom; padding-bottom: 5mm; }
    @bottom-left { content: none; }
    @bottom-center { content: url(/bsm-logo-print.svg); vertical-align: top; padding-top: 4mm; }
    @bottom-right { content: "${esc(pageWord)} " counter(page) " ${esc(ofWord)} " counter(pages); font: 500 8pt Manrope, sans-serif; color: #A8A29E; vertical-align: top; padding-top: 5mm; }
  } }`;
  return <style>{css}</style>;
}

export const STATUS_COLOR = (s) => {
  const k = (s || "").toLowerCase();
  if (/lukket|færdig|afsluttet|accepteret|godkendt|virker$|opfyldt$|behandlet/.test(k)) return "#5f6b4a";
  if (/forsinket|overskredet|afvist|virker ikke|ikke opfyldt|kritisk/.test(k)) return "#A83232";
  if (/i gang|aktiv|følg op|delvist|afventer|udsat|pauset/.test(k)) return "#806B61";
  return "#A8A29E";
};
export const PStatus = ({ s, label, color }) => s || label ? <span className="pd-status"><span className="pd-dot" style={{ background: color || STATUS_COLOR(s) }} />{label || s}</span> : <span className="pd-muted">—</span>;
export const POverdue = ({ children = "Overskredet" }) => <span className="pd-overdue">{children}</span>;

export function PDoc({ children, rtl, className = "" }) { return <div className={`pdoc ${className}`.trim()} dir={rtl ? "rtl" : undefined} data-testid="print-doc">{children}</div>; }

export function PTitle({ kicker, title, sub, logo }) {
  return <header className="pd-head">{logo && <img src={BSM_LOGO_PRINT} alt="BSM A/S" className="pd-logo-top" />}<p className="pd-kicker">{kicker}</p><h1 className="pd-title">{title}</h1>{sub && <p className="pd-sub">{sub}</p>}</header>;
}

export function PMeta({ items, title }) {
  const shown = (items || []).filter((m) => m && m.value);
  if (!shown.length) return null;
  return <>{title && <p className="pd-label pd-block-label">{title}</p>}<div className="pd-meta">{shown.map((m, i) => <div key={i}><p className="pd-label">{m.label}</p><p className="pd-value">{m.value}</p></div>)}</div></>;
}

export function PKpis({ items, title = "Nøgletal" }) {
  if (!items?.length) return null;
  return <>{title && <p className="pd-label pd-block-label">{title}</p>}<div className="pd-kpis">{items.map((k, i) => (
    <div key={i} className={`pd-kpi ${i === 0 ? "hero" : ""} ${k.dark ? "dark" : ""}`}><p className="pd-label">{k.label}</p><p className={`pd-kpi-v ${/^\d{2}-\d{2}-\d{4}$/.test(String(k.value)) ? "date" : ""}`}>{k.value}</p>{k.sub && <p className="pd-kpi-s">{k.sub}</p>}</div>
  ))}</div></>;
}

export function PBars({ items, title }) {
  if (!items?.length) return null;
  const max = Math.max(1, ...items.map((b) => b.count || 0));
  const total = items.reduce((s, x) => s + (x.count || 0), 0) || 1;
  return <>{title && <p className="pd-label pd-block-label">{title}</p>}<div className="pd-bars">{items.map((b, i) => (
    <div key={i} className="pd-bar"><span>{b.label}</span><div className="pd-bar-track"><div className="pd-bar-fill" style={{ width: `${Math.round(((b.count || 0) / max) * 100)}%`, background: b.color || "#A83232" }} /></div><span className="pd-bar-n">{b.count} · {Math.round(((b.count || 0) / total) * 100)}%</span></div>
  ))}</div></>;
}

export function PToc({ items, title = "Indholdsfortegnelse" }) {
  if (!items?.length) return null;
  return <><p className="pd-label pd-block-label">{title}</p><ol className="pd-toc">{items.map((t, i) => <li key={i}><span className="pd-num">{/^\d+$/.test(String(t.num ?? i + 1)) ? String(t.num ?? i + 1).padStart(2, "0") : t.num}</span><span>{t.label}{t.note && <span className="pd-muted"> — {t.note}</span>}</span></li>)}</ol></>;
}

export const PCoverBreak = () => <div className="pd-pagebreak" />;

const DATE_RE = /(\d{2}-\d{2}-\d{4})/g;
export const nbDates = (c) => typeof c === "string" && /\d{2}-\d{2}-\d{4}/.test(c) ? c.split(DATE_RE).map((part, i) => i % 2 ? <span key={i} className="nowrap">{part}</span> : part) : c;
export const PSummary = ({ children }) => children ? <p className="pd-summary" data-testid="print-summary">{nbDates(children)}</p> : null;
export function PSection({ num, title, children, empty, note }) {
  let kids = Children.toArray(children).filter(Boolean);
  const isSum = (k) => k && k.type === PSummary;
  const ti = kids.findIndex((k) => k && k.type === PTable && k.props.split && (k.props.rows || []).length > 3);
  if (ti >= 0 && ti <= 1) {
    const t = kids[ti]; const rows = t.props.rows; const chunks = [];
    for (let i = 2; i < rows.length; i += 2) chunks.push(rows.slice(i, i + 2));
    if (chunks.length > 1 && chunks[chunks.length - 1].length === 1) { const l = chunks.pop(); chunks[chunks.length - 1].push(...l); }
    kids = [...kids.slice(0, ti), <PTable key="t0" {...t.props} split={false} rows={rows.slice(0, 2)} keep />, ...chunks.map((c, i) => <PTable key={`t${i + 1}`} {...t.props} split={false} rows={c} keep noHead />), ...kids.slice(ti + 1)];
  }
  const headN = kids.length ? (isSum(kids[0]) ? (kids[1] && kids[1].type === PEntry && kids[2] && kids[2].type === PEntry ? 3 : 2) : 1) : 0;
  const head = kids.slice(0, headN), rest = kids.slice(headN);
  return (
    <section className="pd-section">
      <div className="pd-keep">
      <h2 className="pd-h2"><span className="pd-num">{/^\d+$/.test(String(num)) ? String(num).padStart(2, "0") : num}</span>{title}{empty && <span className="pd-h2-empty"> — {empty}</span>}{note && <span className="pd-h2-note">{note}</span>}</h2>
      {!empty && head}
      </div>
      {!empty && rest}
    </section>
  );
}

export const PText = ({ children, accent, className = "" }) => children ? <div className={`pd-text ${accent ? "accent" : ""} ${className}`}>{tidy(children)}</div> : null;
export const PH3 = ({ children }) => <h3 className="pd-h3">{children}</h3>;

export function PEntry({ no, title, value, meta, children, status }) {
  return <div className="pd-entry"><p className="pd-entry-t">{status && <span className="pd-dot" style={{ background: STATUS_COLOR(status) }} />}{no && <span className="pd-mono">{no} · </span>}{title}{value && <span className="pd-mono"> · {value}</span>}</p>{meta && <p className="pd-entry-m">{meta.filter ? meta.filter(Boolean).join(" · ") : meta}</p>}{children}</div>;
}
const tidy = (c) => typeof c === "string" ? nbDates(c.replace(/[ \t]+\n/g, "\n").replace(/\n{2,}/g, "\n").trim()) : c;
export const PBody = ({ children, label }) => children ? <p className="pd-entry-b">{label && <span className="pd-muted">{label} </span>}{tidy(children)}</p> : null;
export const PGroup = ({ children }) => <div className="pd-group">{children}</div>;
export function PPairs({ items, render }) {
  const out = []; for (let i = 0; i < items.length; i += 2) out.push(items.slice(i, i + 2));
  if (out.length > 1 && out[out.length - 1].length === 1) { const l = out.pop(); out[out.length - 1].push(...l); }
  return out.map((pair, i) => <div key={i} className="pd-group">{pair.map(render)}</div>);
}
export const PAppendix = ({ letter, title, children }) => <section className="pd-section pd-appendix"><h2 className="pd-h2"><span className="pd-num">Bilag {letter}</span>{title}</h2>{children}</section>;
function Table({ cols, rows, keep, noHead, testid }) {
  const cls = "pd-table " + (keep ? "keep" : "breakable") + (noHead ? " nohead" : "");
  return (
    <table className={cls} data-testid={testid}>
      <colgroup>{cols.map((c, i) => <col key={i} style={c.w ? { width: c.w } : undefined} />)}</colgroup>
      {!noHead && <thead><tr>{cols.map((c, i) => <th key={i} className={c.cls} style={{ textAlign: c.align || "start" }}>{c.label}</th>)}</tr></thead>}
      <tbody>{rows.map((r, ri) => <tr key={r.id || ri}>{cols.map((c, ci) => <td key={ci} style={{ textAlign: c.align || "start" }} className={(c.cls || "") + (r._indent ? " indent" : "")}>{c.render ? c.render(r, ri) : r[c.key]}</td>)}</tr>)}</tbody>
    </table>
  );
}
export function PTable({ cols, rows, empty = "Ingen poster", testid, keep, split, noHead }) {
  if (!rows?.length) return <p className="pd-muted pd-empty">{empty}</p>;
  if (split && rows.length > 3) {
    const rest = rows.slice(2), chunks = [];
    for (let i = 0; i < rest.length; i += 2) chunks.push(rest.slice(i, i + 2));
    if (chunks.length > 1 && chunks[chunks.length - 1].length === 1) { const last = chunks.pop(); chunks[chunks.length - 1].push(...last); }
    return [<Table key="h" cols={cols} rows={rows.slice(0, 2)} keep testid={testid} />, ...chunks.map((c, i) => <Table key={i} cols={cols} rows={c} keep noHead />)];
  }
  return <Table cols={cols} rows={rows} keep={keep} noHead={noHead} testid={testid} />;
}

export function PImage({ src, caption, children }) {
  return <figure className="pd-figure"><div className="pd-figure-wrap"><img src={src} alt={caption || ""} />{children}</div>{caption && <figcaption>{caption}</figcaption>}</figure>;
}

export const PNote = ({ label, children }) => children ? <p className="pd-note">{label && <span className="pd-note-l">{label} </span>}{children}</p> : null;
